import { ReactElement, useMemo } from "react";
import { ValidGlobalSearchItemPersistedModel } from "../../Persistence/validation";
import { GlobalSearchResultItemKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { SearchListItem } from "client/Features/Search/SearchListItem";
import { generateTrackingId } from "client/Misc/utils";
import { useAnnouncementNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useAnnouncementNavigationHandler";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { HubItemCategoryListItemModel } from "client/Api/Hub/hub";
import { TimeZone } from "client/DateAndTime/timezones";
import { AnnouncementListItem } from "client/Features/Hub/Announcements/ListItem/AnnouncementListItem";
import { convertHubCategory, convertHubMedia } from "../../Persistence/convertPersistedData";

export type AnnouncementItemViewProps = {
	item: ValidGlobalSearchItemPersistedModel<GlobalSearchResultItemKind.Announcement>;
	navigationIndex: number;
	timezone: TimeZone;
	onRemove: () => void;
};

export const AnnouncementItemView = (props: AnnouncementItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		timezone,
		onRemove
	} = props;

	const {
		id,
		title,
		authorName,
		categories: persistedCategories,
		media: persistedMedia,
		publicationStart
	} = item.announcement;

	const media = useMemo(
		(): HubItemMediaPreviewViewModel => convertHubMedia(persistedMedia),
		[persistedMedia]
	);

	const categories = useMemo(
		(): readonly HubItemCategoryListItemModel[] => persistedCategories.map(c => convertHubCategory(c)),
		[persistedCategories]
	);

	const executeHandler: () => void = useAnnouncementNavigationHandler({
		id,
		recentlyOpenedItem: item
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("announcement-item", `|${id}`)}
		>
			<AnnouncementListItem
				title={title}
				authorName={authorName}
				categories={categories}
				media={media}
				publicationStart={publicationStart}
				trackingId={generateTrackingId("announcement-result-list-item", `|${id}`)}
				onRemove={onRemove}
				timezone={timezone}
				itemIndex={navigationIndex}
				tabIndex={-1}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
AnnouncementItemView.displayName = "AnnouncementItemView";